import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/utils/helpers';
import { toFeedbacksApiError } from '../api/feedbacksApi';
import FeedbackRating from './FeedbackRating';
import type { Feedback, UpdateFeedbackInput } from '../types';

export type FeedbackModalMode = 'view' | 'edit';

interface FeedbackModalProps {
  open: boolean;
  mode: FeedbackModalMode;
  feedback: Feedback | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (input: UpdateFeedbackInput) => Promise<void>;
}

const safeFormatDate = (value: string | null): string => {
  if (!value || Number.isNaN(new Date(value).getTime())) return '—';
  return formatDate(value);
};

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  open,
  mode,
  feedback,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [commentError, setCommentError] = useState('');
  const [serverError, setServerError] = useState('');
  const isReadOnly = mode === 'view';

  useEffect(() => {
    if (!open) return;
    setComment(feedback?.comment ?? '');
    setRating(feedback?.rating ?? null);
    setCommentError('');
    setServerError('');
  }, [feedback, open]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isSubmitting, onClose, open]);

  if (!open || !feedback) return null;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isReadOnly) return;
    if (comment.length > 1000) {
      setCommentError(t('admin.feedbacks.validation.comment'));
      return;
    }

    setServerError('');
    try {
      await onSubmit({
        comment: comment.trim(),
        rating: rating ?? undefined,
      });
      onClose();
    } catch (error: unknown) {
      const normalized = toFeedbacksApiError(error);
      setServerError(normalized.message);
      const backendCommentError = normalized.fieldErrors?.comment?.[0];
      if (backendCommentError) setCommentError(backendCommentError);
    }
  };

  const username = feedback.username?.trim() || t('admin.feedbacks.customer_fallback');

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-3 sm:p-5"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onClose();
      }}
    >
      <section
        className="flex max-h-[92vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[20px] border border-outline-variant bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
      >
        <header className="flex items-start justify-between gap-3 border-b border-outline-variant px-4 py-4 sm:px-6">
          <div>
            <h2 id="feedback-modal-title" className="font-h2 text-h2 text-on-surface">
              {t(isReadOnly ? 'admin.feedbacks.modal.view_title' : 'admin.feedbacks.modal.edit_title')}
            </h2>
            <p className="mt-1 text-body-sm text-secondary">{t('admin.feedbacks.modal.description', { id: feedback.id })}</p>
          </div>
          <button
            type="button"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-secondary hover:bg-surface-container-low disabled:opacity-50"
            disabled={isSubmitting}
            aria-label={t('admin.feedbacks.actions.close')}
            onClick={onClose}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <form id="feedback-form" className="overflow-y-auto px-4 py-4 sm:px-6" onSubmit={handleSubmit}>
          {serverError && (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-error/30 bg-error-container/40 px-3 py-2 text-body-sm text-error" role="alert">
              <span className="material-symbols-outlined text-[19px]">error</span>
              <span>{serverError}</span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-xl bg-surface-container-low p-3">
              <p className="font-label-sm text-secondary">{t('admin.feedbacks.fields.customer')}</p>
              <p className="mt-1 font-medium text-on-surface">{username}</p>
            </div>
            <div className="rounded-xl bg-surface-container-low p-3">
              <p className="font-label-sm text-secondary">{t('admin.feedbacks.fields.product')}</p>
              <p className="mt-1 font-medium text-on-surface">{t('admin.feedbacks.product_id', { id: feedback.productId })}</p>
            </div>

            <div className="sm:col-span-2">
              <p className="font-label-md text-on-surface">{t('admin.feedbacks.fields.rating')}</p>
              {isReadOnly ? (
                <div className="mt-2"><FeedbackRating rating={feedback.rating} /></div>
              ) : (
                <div className="mt-2 flex flex-wrap items-center gap-1" role="radiogroup" aria-label={t('admin.feedbacks.fields.rating')}>
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={rating === value}
                      aria-label={t('admin.feedbacks.rating.choose', { rating: value })}
                      className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border transition-colors ${rating !== null && value <= rating ? 'border-rating/30 bg-rating-container text-rating' : 'border-outline-variant text-outline hover:bg-surface-container-low'}`}
                      disabled={isSubmitting}
                      onClick={() => setRating(value)}
                    >
                      <span className="material-symbols-outlined" style={rating !== null && value <= rating ? { fontVariationSettings: "'FILL' 1" } : {}}>star</span>
                    </button>
                  ))}
                  <span className="ml-2 font-label-md text-secondary">{rating ? `${rating}/5` : t('admin.feedbacks.rating.none')}</span>
                </div>
              )}
            </div>

            <label className="flex flex-col gap-1.5 font-label-md text-on-surface sm:col-span-2">
              {t('admin.feedbacks.fields.comment')}
              {isReadOnly ? (
                <div className="min-h-28 whitespace-pre-wrap rounded-xl border border-outline-variant bg-surface-container-lowest px-3 py-3 font-body-md text-on-surface">
                  {feedback.comment?.trim() || t('admin.feedbacks.comment_fallback')}
                </div>
              ) : (
                <>
                  <textarea
                    className="min-h-36 w-full resize-y rounded-xl border border-outline-variant bg-white px-3 py-2.5 font-body-md text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:bg-surface-container-low"
                    value={comment}
                    maxLength={1001}
                    disabled={isSubmitting}
                    onChange={(event) => {
                      setComment(event.target.value);
                      setCommentError('');
                    }}
                  />
                  <span className={`text-right text-body-sm font-normal ${comment.length > 1000 ? 'text-error' : 'text-secondary'}`}>{comment.length}/1000</span>
                  {commentError && <span className="text-body-sm font-normal text-error">{commentError}</span>}
                </>
              )}
            </label>

            {isReadOnly && (
              <>
                <div>
                  <p className="font-label-sm text-secondary">{t('admin.feedbacks.fields.created_at')}</p>
                  <p className="mt-1 text-on-surface">{safeFormatDate(feedback.createAt)}</p>
                </div>
                <div>
                  <p className="font-label-sm text-secondary">{t('admin.feedbacks.fields.updated_at')}</p>
                  <p className="mt-1 text-on-surface">{safeFormatDate(feedback.updateAt)}</p>
                </div>
              </>
            )}
          </div>
        </form>

        <footer className="flex flex-col-reverse gap-2 border-t border-outline-variant bg-surface-container-lowest px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
          <button
            type="button"
            className="min-h-10 rounded-xl border border-outline-variant bg-white px-4 font-label-md text-secondary hover:bg-surface-container-low disabled:opacity-50"
            disabled={isSubmitting}
            onClick={onClose}
          >
            {t(isReadOnly ? 'admin.feedbacks.actions.close' : 'admin.feedbacks.actions.cancel')}
          </button>
          {!isReadOnly && (
            <button
              type="submit"
              form="feedback-form"
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-primary px-4 font-label-md text-on-primary hover:bg-on-primary-fixed-variant disabled:opacity-60"
              disabled={isSubmitting}
            >
              {isSubmitting && <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>}
              {t(isSubmitting ? 'admin.feedbacks.actions.saving' : 'admin.feedbacks.actions.save')}
            </button>
          )}
        </footer>
      </section>
    </div>
  );
};

export default FeedbackModal;
